// =============================================================================
// STORY GALLERY COMPONENT
// =============================================================================

import React from 'react';
import { Button } from '@/components/Button';
import { createActionHandler, convertButtonToAction } from '@/utils/actionHandler';
import type { ContentComponent } from '@/types';

interface StoryGalleryProps {
  component: ContentComponent;
}

/**
 * Story Gallery Component
 */
export const StoryGallery: React.FC<StoryGalleryProps> = ({ component }) => {
  return <StoryGalleryImpl component={component} />;
};

/**
 * Story Gallery Implementation
 */
const StoryGalleryImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const buttons = metadata?.buttons || [];
  const galleryItems = metadata?.galleryItems || [];
  const columns = metadata?.columns || 3;
  const showTitles = metadata?.showTitles !== false;
  const showDescriptions = metadata?.showDescriptions !== false;
  const showCategories = metadata?.showCategories !== false;
  const showTags = metadata?.showTags !== false;
  const imageHeight = metadata?.imageHeight || '300px';
  const gap = metadata?.gap || 'md';
  
  const actions = buttons.map(convertButtonToAction);
  
  return (
    <section id={id} className="story-gallery">
      <div className="container">
        {(title || content) && (
          <div className="story-gallery-header">
            {title && <h2 className="story-gallery-title">{title}</h2>}
            {content && <p className="story-gallery-description">{content}</p>}
          </div>
        )}
        
        {galleryItems.length > 0 && (
          <div
            className={`story-gallery-grid story-gallery-grid--${columns}-cols story-gallery-grid--gap-${gap}`}
            style={{ '--image-height': imageHeight } as React.CSSProperties}
          >
            {galleryItems.map((item: any, index: number) => {
              const itemAction = item.button ? convertButtonToAction(item.button) : null;
              
              return (
                <div key={item.id || index} className="story-gallery-item">
                  <div className="story-gallery-image-container">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="story-gallery-image"
                      loading="lazy"
                    />
                    {item.category && showCategories && (
                      <div className="story-gallery-category">
                        {item.category}
                      </div>
                    )}
                  </div>
                  
                  <div className="story-gallery-content">
                    {showTitles && item.title && (
                      <h3 className="story-gallery-item-title">{item.title}</h3>
                    )}
                    
                    {showDescriptions && item.description && (
                      <p className="story-gallery-item-description">{item.description}</p>
                    )}
                    
                    {showTags && item.tags && item.tags.length > 0 && (
                      <div className="story-gallery-tags">
                        {item.tags.map((tag: string, tagIndex: number) => (
                          <span key={tagIndex} className="story-gallery-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {itemAction && (
                      <div className="story-gallery-item-actions">
                        <Button
                          variant={itemAction.variant || 'secondary'}
                          size={itemAction.size || 'sm'}
                          onClick={createActionHandler(itemAction)}
                        >
                          {itemAction.label}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {actions.length > 0 && (
          <div className="story-gallery-actions">
            {actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || 'primary'}
                size={action.size || 'lg'}
                onClick={createActionHandler(action)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export { StoryGalleryImpl };

